import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { forwardRef, useMemo, type ChangeEventHandler } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { InputWithLabels } from "../../DataInput/_components/InputWithLabels";
import { IconLoading } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import {
  mergeProps,
  stripInvalidPropsForElement,
} from "../../utils/component-props";
import styles from "./TextAreaInput.module.scss";
import type { ControlledTextAreaInputProps, TextAreaInputProps } from "./types";

const cx = classNames.bind(styles);

const TextAreaInput = forwardRef<HTMLTextAreaElement, TextAreaInputProps>(
  (
    {
      id,
      name,
      value = "",
      placeholder = "",
      showIcon = false,
      isDisabled = false,
      disabled = false,
      isLoading = false,
      isBusy = false,
      isInline = false,
      invalidMessage,
      error,
      "aria-invalid": ariaInvalid,
      flairSlot,
      onChange,
      onBlur,
      className = "",
      style,
      ...restProps
    },
    ref
  ) => {
    const handleOnChange: ChangeEventHandler<HTMLTextAreaElement> = (evt) => {
      onChange?.(evt.target.value);
    };

    return (
      <Skeleton
        isLoading={isLoading}
        isBusy={isBusy}
      >
        <div
          className={cx("inputContainer", className)}
          style={style}
        >
          <textarea
            id={id}
            name={name}
            className={cx("input", {
              "input--invalid":
                ariaInvalid === true || !!invalidMessage || !!error,
              "input--disabled": isDisabled || disabled,
              "input--truncated": showIcon,
              "input--inline": isInline,
            })}
            value={value}
            placeholder={placeholder}
            ref={ref}
            onChange={handleOnChange}
            onBlur={onBlur}
            disabled={isDisabled || disabled}
            aria-invalid={ariaInvalid}
            {...stripInvalidPropsForElement(restProps, "textarea")}
          />

          {(isBusy || flairSlot) && (
            <div className={cx("flair")}>
              {isBusy && (
                <IconLoading
                  speed="fast"
                  size={10}
                />
              )}
              {flairSlot?.({})}
            </div>
          )}
        </div>
      </Skeleton>
    );
  }
);

TextAreaInput.displayName = "TextAreaInput";

const ControlledTextAreaInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledTextAreaInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Text input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot }) => (
        <TextAreaInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          flairSlot={flairSlot}
          isInline={isInline}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledTextAreaInput.displayName = "ControlledTextAreaInput";

export { ControlledTextAreaInput };
export default TextAreaInput;
